var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import GET_ORDER_SUPPLIERS from 'api/queries/GET_ORDER_SUPPLIERS';
import GET_SPECIFICATION_PRODUCTION_MANAGER_DETAILS from 'api/queries/GET_SPECIFICATION_PRODUCTION_MANAGER_DETAILS';
import TimelineErrorIcon from 'images/timeline-error.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import ChevronRightSvg from 'images/v5/icons/chevron-right.svg';
import Loader from 'layout/atoms/Loader/Loader';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import Tooltip from 'layout/atoms/Tooltip';
import Grid from 'layout/molecules/Grid/Grid';
import SimpleTimeline from 'layout/molecules/SimpleTimeline';
import { selectOrderDetailsItem, selectOrderDetailsStatus } from 'store/orderDetailsSlice';
import { fetchOrderProductionManagerData, resetOrderProductionManagerState, selectOrderProductionManagerData, selectOrderProductionManagerRefetchFlags, selectOrderProductionManagerStatus, } from 'store/orderProductionManagerSlice';
import styles from './Planning.module.scss';
const UNASSIGNED_MANUFACTURER_ID = 'unassigned-manufacturer';
const WORKING_DAY_MINUTES_PER_EMPLOYEE = 480;
const getNonWorkingDateKeys = (nonWorkingDays = []) => {
    const nonWorkingDateKeys = new Set();
    nonWorkingDays.forEach((range) => {
        if (!(range === null || range === void 0 ? void 0 : range.startDate) || !(range === null || range === void 0 ? void 0 : range.endDate)) {
            return;
        }
        const start = moment(range.startDate).startOf('day');
        const end = moment(range.endDate).startOf('day');
        if (!start.isValid() || !end.isValid()) {
            return;
        }
        const cursor = start.clone();
        while (cursor.isSameOrBefore(end, 'day')) {
            nonWorkingDateKeys.add(cursor.format('YYYY-MM-DD'));
            cursor.add(1, 'day');
        }
    });
    return nonWorkingDateKeys;
};
const isManufacturerWorkingDay = (date, nonWorkingDateKeys) => date.isoWeekday() < 6 && !nonWorkingDateKeys.has(date.format('YYYY-MM-DD'));
const calculateVirtualProductionEndDate = ({ productionStartDate, totalProductionTimeMinutes, manufacturerEmployees, manufacturerNonWorkingDays, }) => {
    if (!productionStartDate.isValid() ||
        !totalProductionTimeMinutes ||
        totalProductionTimeMinutes <= 0 ||
        !manufacturerEmployees ||
        manufacturerEmployees <= 0) {
        return null;
    }
    const nonWorkingDateKeys = getNonWorkingDateKeys(manufacturerNonWorkingDays);
    const dailyCapacityMinutes = manufacturerEmployees * WORKING_DAY_MINUTES_PER_EMPLOYEE;
    const cursor = productionStartDate.clone().startOf('day');
    let remainingProductionMinutes = totalProductionTimeMinutes;
    let safetyCounter = 0;
    while (remainingProductionMinutes > 0 && safetyCounter < 3660) {
        if (isManufacturerWorkingDay(cursor, nonWorkingDateKeys)) {
            remainingProductionMinutes -= dailyCapacityMinutes;
        }
        if (remainingProductionMinutes > 0) {
            cursor.add(1, 'day');
        }
        safetyCounter += 1;
    }
    if (remainingProductionMinutes > 0) {
        return null;
    }
    return cursor.endOf('day');
};
function Planning() {
    var _a, _b, _c;
    const apolloClient = useApolloClient();
    const { t } = useTranslation(['orders', 'common']);
    const dispatch = useDispatch();
    const [timelinePeriod, setTimelinePeriod] = useState('week');
    const [expandedManufacturerIds, setExpandedManufacturerIds] = useState([]);
    const [latestPlannedDeliveryDatesBySpec, setLatestPlannedDeliveryDatesBySpec] = useState({});
    const latestPlannedDeliveryRequestRef = useRef('');
    const orderDetailsStatus = useSelector(selectOrderDetailsStatus);
    const pmStatus = useSelector(selectOrderProductionManagerStatus);
    const pmRefetchFlag = useSelector(selectOrderProductionManagerRefetchFlags).fetchOrderProductionManagerData;
    const orderItem = useSelector(selectOrderDetailsItem);
    const orderProductionManagerData = useSelector(selectOrderProductionManagerData);
    useEffect(() => {
        return () => {
            dispatch(resetOrderProductionManagerState());
        };
    }, [dispatch]);
    useEffect(() => {
        var _a;
        const hasDataForDifferentOrder = ((_a = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.order) === null || _a === void 0 ? void 0 : _a.id) &&
            (orderItem === null || orderItem === void 0 ? void 0 : orderItem.id) &&
            orderProductionManagerData.order.id !== orderItem.id;
        if ((orderDetailsStatus.fetch === 'succeeded' &&
            (pmStatus.fetchOrderProductionManagerData === 'idle' || hasDataForDifferentOrder)) ||
            pmRefetchFlag) {
            dispatch(fetchOrderProductionManagerData(orderItem.id));
        }
    }, [
        dispatch,
        orderItem === null || orderItem === void 0 ? void 0 : orderItem.id,
        orderDetailsStatus.fetch,
        pmStatus.fetchOrderProductionManagerData,
        pmRefetchFlag,
        (_a = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.order) === null || _a === void 0 ? void 0 : _a.id,
    ]);
    useEffect(() => {
        var _a;
        const orderId = ((_a = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.order) === null || _a === void 0 ? void 0 : _a.id) || (orderItem === null || orderItem === void 0 ? void 0 : orderItem.id);
        const specificationIds = Array.from(new Set(((orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.specifications) || [])
            .filter((spec) => !spec.productionStartDate)
            .map((spec) => { var _a; return (_a = spec.specification) === null || _a === void 0 ? void 0 : _a.id; })
            .filter(Boolean)));
        if (!orderId || specificationIds.length === 0) {
            latestPlannedDeliveryRequestRef.current = '';
            setLatestPlannedDeliveryDatesBySpec({});
            return undefined;
        }
        const requestKey = `${orderId}:${specificationIds.join('|')}`;
        latestPlannedDeliveryRequestRef.current = requestKey;
        let isCurrentRequest = true;
        const fetchLatestPlannedDeliveryDates = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                const suppliersResponse = yield apolloClient.query({
                    query: GET_ORDER_SUPPLIERS,
                    variables: { id: orderId },
                    fetchPolicy: 'network-only',
                });
                if (!isCurrentRequest || latestPlannedDeliveryRequestRef.current !== requestKey) {
                    return;
                }
                const plannedDatesByVariantId = new Map();
                (((_b = (_a = suppliersResponse.data) === null || _a === void 0 ? void 0 : _a.node) === null || _b === void 0 ? void 0 : _b.componentVariantsBySuppliers) || []).forEach((supplierGroup) => {
                    (supplierGroup.componentVariants || []).forEach((componentVariant) => {
                        if (!(componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.id)) {
                            return;
                        }
                        const plannedDates = (componentVariant.deliveries || [])
                            .filter((delivery) => delivery.deliveryType === 'PLANNED' &&
                            delivery.plannedDate &&
                            moment(delivery.plannedDate).isValid())
                            .map((delivery) => delivery.plannedDate);
                        if (plannedDates.length === 0) {
                            return;
                        }
                        const existingDates = plannedDatesByVariantId.get(componentVariant.id) || [];
                        plannedDatesByVariantId.set(componentVariant.id, [...existingDates, ...plannedDates]);
                    });
                });
                if (plannedDatesByVariantId.size === 0) {
                    setLatestPlannedDeliveryDatesBySpec({});
                    return;
                }
                const specificationDetailsResponses = yield Promise.all(specificationIds.map((specificationId) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    try {
                        const response = yield apolloClient.query({
                            query: GET_SPECIFICATION_PRODUCTION_MANAGER_DETAILS,
                            variables: { orderId, specificationId },
                            fetchPolicy: 'network-only',
                        });
                        return {
                            specificationId,
                            details: (_a = response.data) === null || _a === void 0 ? void 0 : _a.specificationProductionManagerDetails,
                        };
                    }
                    catch (_b) {
                        return {
                            specificationId,
                            details: null,
                        };
                    }
                })));
                if (!isCurrentRequest || latestPlannedDeliveryRequestRef.current !== requestKey) {
                    return;
                }
                const latestDatesBySpec = {};
                specificationDetailsResponses.forEach(({ specificationId, details }) => {
                    const latestPlannedDeliveryDate = ((details === null || details === void 0 ? void 0 : details.components) || [])
                        .flatMap((component) => component.componentVariants || [])
                        .filter((componentVariant) => {
                        const allocatedQuantity = Number((componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.allocatedQuantity) || 0);
                        const requiredConsumption = Number((componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.requiredConsumption) || 0);
                        return (componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.id) && (allocatedQuantity > 0 || requiredConsumption > 0);
                    })
                        .flatMap((componentVariant) => plannedDatesByVariantId.get(componentVariant.id) || [])
                        .map((plannedDate) => moment(plannedDate))
                        .filter((plannedDateMoment) => plannedDateMoment.isValid())
                        .reduce((latestDate, plannedDateMoment) => !latestDate || plannedDateMoment.isAfter(latestDate)
                        ? plannedDateMoment
                        : latestDate, null);
                    if (latestPlannedDeliveryDate !== null) {
                        latestDatesBySpec[specificationId] = latestPlannedDeliveryDate.toISOString();
                    }
                });
                setLatestPlannedDeliveryDatesBySpec(latestDatesBySpec);
            }
            catch (_c) {
                if (isCurrentRequest && latestPlannedDeliveryRequestRef.current === requestKey) {
                    setLatestPlannedDeliveryDatesBySpec({});
                }
            }
        });
        fetchLatestPlannedDeliveryDates();
        return () => {
            isCurrentRequest = false;
        };
    }, [
        apolloClient,
        orderItem === null || orderItem === void 0 ? void 0 : orderItem.id,
        (_b = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.order) === null || _b === void 0 ? void 0 : _b.id,
        orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.specifications,
    ]);
    const pixelsPerDayMap = {
        day: 60,
        week: 30,
        month: 15,
    };
    const getMissingReasons = useCallback((spec) => {
        var _a, _b, _c, _d;
        const totalProductionTimeMinutes = (_a = spec.totalProductionTimeMinutes) !== null && _a !== void 0 ? _a : null;
        const missingReasons = [];
        const missingManufacturer = !((_b = spec.manufacturer) === null || _b === void 0 ? void 0 : _b.id);
        if (!spec.productionStartDate) {
            missingReasons.push(t('orders:planning_missing_production_start_date'));
        }
        if (!((_c = spec.specification) === null || _c === void 0 ? void 0 : _c.productionTimeMinutes)) {
            missingReasons.push(t('orders:planning_missing_production_time'));
        }
        if (!spec.sumOfModelQuantities) {
            missingReasons.push(t('orders:planning_missing_order_quantity'));
        }
        else if (!totalProductionTimeMinutes) {
            missingReasons.push(t('orders:planning_missing_production_time'));
        }
        if (missingManufacturer) {
            missingReasons.push(t('orders:planning_missing_manufacturer'));
        }
        else if (!((_d = spec.manufacturer) === null || _d === void 0 ? void 0 : _d.employeesCount)) {
            missingReasons.push(t('orders:planning_missing_manufacturer_employees'));
        }
        return {
            missingReasons,
            missingManufacturer,
        };
    }, [t]);
    const timelineGroups = useMemo(() => {
        const manufacturerGroups = new Map();
        ((orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.specifications) || []).forEach((spec, index) => {
            var _a, _b, _c, _d, _e, _f;
            const manufacturerId = ((_a = spec.manufacturer) === null || _a === void 0 ? void 0 : _a.id) || UNASSIGNED_MANUFACTURER_ID;
            const manufacturerName = ((_b = spec.manufacturer) === null || _b === void 0 ? void 0 : _b.shortName) ||
                ((_c = spec.manufacturer) === null || _c === void 0 ? void 0 : _c.fullName) ||
                t('orders:planning_no_manufacturer');
            if (!manufacturerGroups.has(manufacturerId)) {
                manufacturerGroups.set(manufacturerId, {
                    id: manufacturerId,
                    name: manufacturerName,
                    specifications: [],
                });
            }
            const { missingReasons, missingManufacturer } = getMissingReasons(spec);
            manufacturerGroups.get(manufacturerId).specifications.push(Object.assign(Object.assign({}, spec), { rowId: ((_d = spec.specification) === null || _d === void 0 ? void 0 : _d.id) || `${manufacturerId}-${index}`, title: ((_e = spec.specification) === null || _e === void 0 ? void 0 : _e.fancyName) || ((_f = spec.specification) === null || _f === void 0 ? void 0 : _f.itemCode) || '-', missingReasons,
                missingManufacturer, hasMissingData: missingReasons.length > 0 }));
        });
        return Array.from(manufacturerGroups.values()).flatMap((manufacturer) => {
            const isExpanded = expandedManufacturerIds.includes(manufacturer.id);
            const productionApproved = manufacturer.specifications.every((spec) => spec.productionApproved === true);
            const manufacturerRow = {
                id: `manufacturer-${manufacturer.id}`,
                manufacturerId: manufacturer.id,
                title: manufacturer.name,
                type: 'manufacturer',
                isExpanded,
                statusLabel: productionApproved
                    ? t('orders:planning_supplier_status_approved')
                    : t('orders:planning_supplier_status_planned'),
                statusType: productionApproved ? 'approved' : 'planned',
            };
            if (!isExpanded) {
                return [manufacturerRow];
            }
            return [
                manufacturerRow,
                ...manufacturer.specifications.map((spec) => ({
                    id: spec.rowId,
                    title: spec.title,
                    type: 'specification',
                    statusValue: spec.productionApproved ? 100 : 0,
                    hasMissingData: spec.hasMissingData,
                    missingManufacturer: spec.missingManufacturer,
                    missingReasons: spec.missingReasons,
                })),
            ];
        });
    }, [expandedManufacturerIds, getMissingReasons, orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.specifications, t]);
    const timelineEvents = useMemo(() => ((orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.specifications) || [])
        .map((spec) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
        const backendVirtualEndDate = spec.virtualProductionEndDate
            ? moment(spec.virtualProductionEndDate)
            : null;
        if (!((_a = spec.specification) === null || _a === void 0 ? void 0 : _a.id)) {
            return null;
        }
        const specificationName = ((_b = spec.specification) === null || _b === void 0 ? void 0 : _b.fancyName) || ((_c = spec.specification) === null || _c === void 0 ? void 0 : _c.itemCode) || '-';
        const manufacturerName = ((_d = spec.manufacturer) === null || _d === void 0 ? void 0 : _d.shortName) || ((_e = spec.manufacturer) === null || _e === void 0 ? void 0 : _e.fullName) || '-';
        const manufacturerEmployees = (_g = (_f = spec.manufacturer) === null || _f === void 0 ? void 0 : _f.employeesCount) !== null && _g !== void 0 ? _g : 0;
        const manufacturerId = ((_h = spec.manufacturer) === null || _h === void 0 ? void 0 : _h.id) || null;
        const manufacturerNonWorkingDays = ((_j = spec.manufacturer) === null || _j === void 0 ? void 0 : _j.nonWorkingDays) || [];
        if (!spec.productionStartDate) {
            const latestPlannedDeliveryDate = latestPlannedDeliveryDatesBySpec[spec.specification.id]
                ? moment(latestPlannedDeliveryDatesBySpec[spec.specification.id])
                : null;
            if (!(latestPlannedDeliveryDate === null || latestPlannedDeliveryDate === void 0 ? void 0 : latestPlannedDeliveryDate.isValid())) {
                return null;
            }
            const eventStartDate = latestPlannedDeliveryDate.clone().startOf('day');
            const virtualEndDate = (backendVirtualEndDate === null || backendVirtualEndDate === void 0 ? void 0 : backendVirtualEndDate.isValid()) === true
                ? backendVirtualEndDate.clone()
                : calculateVirtualProductionEndDate({
                    productionStartDate: eventStartDate,
                    totalProductionTimeMinutes: (_k = spec.totalProductionTimeMinutes) !== null && _k !== void 0 ? _k : null,
                    manufacturerEmployees,
                    manufacturerNonWorkingDays,
                });
            if (!(virtualEndDate === null || virtualEndDate === void 0 ? void 0 : virtualEndDate.isValid())) {
                return null;
            }
            const eventEndDate = virtualEndDate.clone().startOf('day');
            let timelineEndDate = virtualEndDate.clone().endOf('day');
            const durationDays = Math.max(0, eventEndDate.diff(eventStartDate, 'days'));
            const virtualProductionTitle = t('orders:planning_event_virtual_production');
            if (timelineEndDate.isBefore(eventStartDate)) {
                timelineEndDate = eventStartDate.clone().endOf('day');
            }
            return {
                id: `virtual-production-${spec.specification.id}`,
                title: virtualProductionTitle,
                simpleTitle: virtualProductionTitle,
                menuType: 'planning-virtual',
                groupId: spec.specification.id,
                specificationName,
                manufacturerName,
                manufacturerEmployees,
                manufacturerId,
                manufacturerNonWorkingDays,
                productionTimeMinutes: (_m = (_l = spec.specification) === null || _l === void 0 ? void 0 : _l.productionTimeMinutes) !== null && _m !== void 0 ? _m : null,
                productionStartDate: spec.productionStartDate,
                orderId: (_o = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.order) === null || _o === void 0 ? void 0 : _o.id,
                specificationId: spec.specification.id,
                sumOfModelQuantities: (_p = spec.sumOfModelQuantities) !== null && _p !== void 0 ? _p : null,
                totalProductionTimeMinutes: (_q = spec.totalProductionTimeMinutes) !== null && _q !== void 0 ? _q : null,
                productionApproved: spec.productionApproved,
                virtualEndDate: virtualEndDate.clone().toISOString(),
                timelinePeriod,
                daysUntilNextEvent: durationDays,
                renderStartOnly: true,
                start_time: eventStartDate.valueOf(),
                end_time: timelineEndDate.valueOf(),
            };
        }
        if (!(backendVirtualEndDate === null || backendVirtualEndDate === void 0 ? void 0 : backendVirtualEndDate.isValid())) {
            return null;
        }
        return {
            id: `virtual-end-${(_r = spec.specification) === null || _r === void 0 ? void 0 : _r.id}`,
            status: spec.productionApproved ? 'done' : undefined,
            title: spec.productionApproved
                ? t('orders:planning_event_production_approved')
                : t('orders:planning_event_waiting_for_approval'),
            menuType: spec.productionApproved ? 'planning-approved' : 'planning',
            groupId: (_s = spec.specification) === null || _s === void 0 ? void 0 : _s.id,
            specificationName,
            manufacturerName,
            manufacturerEmployees,
            manufacturerId,
            manufacturerNonWorkingDays,
            productionTimeMinutes: (_u = (_t = spec.specification) === null || _t === void 0 ? void 0 : _t.productionTimeMinutes) !== null && _u !== void 0 ? _u : null,
            productionStartDate: spec.productionStartDate,
            orderId: (_v = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.order) === null || _v === void 0 ? void 0 : _v.id,
            specificationId: (_w = spec.specification) === null || _w === void 0 ? void 0 : _w.id,
            sumOfModelQuantities: (_x = spec.sumOfModelQuantities) !== null && _x !== void 0 ? _x : null,
            totalProductionTimeMinutes: (_y = spec.totalProductionTimeMinutes) !== null && _y !== void 0 ? _y : null,
            productionApproved: spec.productionApproved,
            virtualEndDate: backendVirtualEndDate.clone().toISOString(),
            timelinePeriod,
            start_time: backendVirtualEndDate.clone().startOf('day').valueOf(),
            end_time: backendVirtualEndDate.clone().endOf('day').valueOf(),
        };
    })
        .filter(Boolean), [
        latestPlannedDeliveryDatesBySpec,
        t,
        orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.specifications,
        (_c = orderProductionManagerData === null || orderProductionManagerData === void 0 ? void 0 : orderProductionManagerData.order) === null || _c === void 0 ? void 0 : _c.id,
        timelinePeriod,
    ]);
    if (pmStatus.fetchOrderProductionManagerData === 'loading' ||
        pmStatus.fetchOrderProductionManagerData === 'idle') {
        return React.createElement(Loader, null);
    }
    if (pmStatus.fetchOrderProductionManagerData === 'failed') {
        return React.createElement("p", null, "error");
    }
    const toggleManufacturer = (manufacturerId) => {
        setExpandedManufacturerIds((current) => current.includes(manufacturerId)
            ? current.filter((id) => id !== manufacturerId)
            : [...current, manufacturerId]);
    };
    const renderStatusIndicator = (type = 'planned') => (React.createElement("span", { className: `${styles.statusIndicator} ${styles[type]}` }));
    return (React.createElement(Grid, { gap: 0, hp: 24, vp: 16 },
        React.createElement("div", { style: {
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                marginBottom: 8,
            } },
            React.createElement(TabsButton, { tabs: [
                    { id: 'day', label: t('common:DAY') },
                    { id: 'week', label: t('common:WEEK') },
                    { id: 'month', label: t('common:MONTH') },
                ], activeTab: timelinePeriod, onClick: (value) => setTimelinePeriod(value) })),
        React.createElement(SimpleTimeline, { groups: timelineGroups, events: timelineEvents, pixelsPerDay: pixelsPerDayMap[timelinePeriod], labelWidth: 260, timelinePeriod: timelinePeriod, renderHeaderLeft: () => (React.createElement("div", { className: styles.leftHeader },
                React.createElement("div", { className: styles.nameHeader },
                    t('common:name'),
                    React.createElement(ChevronDownSvg, { className: styles.sortIcon })),
                React.createElement("div", { className: styles.statusHeader },
                    t('orders:status'),
                    React.createElement(ChevronDownSvg, { className: styles.sortIcon })))), renderGroupLeft: (group) => {
                if (group.type === 'manufacturer') {
                    return (React.createElement("div", { className: styles.manufacturerRow },
                        React.createElement("button", { className: styles.expandButton, type: "button", "aria-expanded": group.isExpanded, onClick: () => toggleManufacturer(group.manufacturerId) }, group.isExpanded ? React.createElement(ChevronDownSvg, null) : React.createElement(ChevronRightSvg, null)),
                        React.createElement("div", { className: styles.nameCell }, group.title),
                        React.createElement("div", { className: styles.statusCell },
                            renderStatusIndicator(group.statusType),
                            React.createElement("span", null, group.statusLabel))));
                }
                return (React.createElement("div", { className: styles.specificationRow },
                    React.createElement("div", { className: styles.branchLine }),
                    React.createElement("div", { className: styles.nameCell },
                        React.createElement("span", null, group.title),
                        group.hasMissingData && (React.createElement(Tooltip, { position: "top", delay: 200, maxWidth: 250, content: group.missingManufacturer ? (React.createElement("div", { style: { maxWidth: 320 } }, t('orders:planning_missing_manufacturer_tooltip'))) : (React.createElement("div", { style: { display: 'grid', gap: 6 } },
                                React.createElement("div", { style: { fontWeight: 600 } }, t('orders:planning_missing_data_title')),
                                React.createElement("ul", { style: { margin: 0, paddingLeft: 18 } }, (group.missingReasons || []).map((reason) => (React.createElement("li", { key: reason }, reason)))))) },
                            React.createElement("span", { className: `${styles.warningBadge} ${group.missingManufacturer ? styles.error : styles.warning}` },
                                React.createElement(TimelineErrorIcon, null))))),
                    React.createElement("div", { className: styles.statusCell },
                        renderStatusIndicator(group.statusValue === 100 ? 'approved' : 'planned'),
                        React.createElement("span", null,
                            group.statusValue,
                            "%"))));
            } })));
}
export default memo(Planning);
